/* globals BaseUrl */
import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { setHelmetTags } from 'helpers/HelmetHelper';
import { browserHistory } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import BreadcrumbsComponent, { breadcrumbsBrowseParser } from 'components/Breadcrumbs';
import Result from 'componentsV2/Result';
import Filter from 'componentsV2/Filter';
import { BROWSE_POSTER } from 'componentsV2/Poster';
import BrowseSEO from 'helpers/SEO/SeoObjects/Browse.json';
import { spaceToDash } from 'helpers/ContentHelper';
import { fetchBrowse, resetBrowse, selectTab, fetchNextPage, setFetching } from './BrowseActions';
import { getFilterName, reduceBrowseResults, getSelectedFilters } from './BrowseProcessor';
import Style from './Browse.module.scss';

const Browse = ({ accessToken, account, deviceId, favoritesManager, location }) => {
  const dispatch = useDispatch();
  const pathName = location?.pathname;
  const { browsePage_header: browseHeader } = window.Language;

  const previousItemsRef = useRef({});
  const previousSelectedFilterRef = useRef('');

  const { browseFilters, selectedTab, browseResults, fetching } = useSelector(
    state => state.browse,
  );

  const filterName = useMemo(() => getFilterName({ pathName }), [pathName]);
  const browseContext = useMemo(() => browseResults?.[selectedTab], [browseResults, selectedTab]);
  const items = browseContext?.items ?? [];
  const nextUrl = browseContext?.next;

  const filters = useMemo(() => getSelectedFilters({ previousItemsRef, browseFilters }), [
    browseFilters,
  ]);

  useEffect(() => {
    // this is needed to show the previously rendered data, till the time new search data is updated
    previousItemsRef.current = { browseResults, browseFilters };
  }, [browseResults, browseFilters]);

  useEffect(() => {
    if (!selectedTab) {
      const firstFilter = browseFilters?.[0];
      if (firstFilter) {
        const currentPath = window?.location?.pathname?.split('/')[1];
        const path = `/${currentPath}/${firstFilter}`.toLowerCase();
        browserHistory.replace(path);
        dispatch(selectTab(firstFilter));
      }
    }
  }, [selectedTab, browseFilters, dispatch]);

  useEffect(() => {
    if (filterName) {
      dispatch(selectTab(filterName));
    }
  }, [filterName, dispatch]);

  useEffect(() => {
    previousSelectedFilterRef.current = selectedTab;
  }, [selectedTab]);

  useEffect(() => {
    dispatch(setFetching(true));
    dispatch(fetchBrowse({ accessToken, deviceId }));
    return () => {
      dispatch(resetBrowse());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const itemLength = browseContext?.items?.length;
    if (itemLength && !nextUrl) {
      dispatch(setFetching(false));
    }
    if (itemLength === 0 && nextUrl) {
      dispatch(setFetching(true));
      dispatch(
        fetchNextPage({
          accessToken,
          deviceId,
          nextUrl,
          filterName: previousSelectedFilterRef.current,
        }),
      );
    }
  }, [accessToken, nextUrl, browseContext, deviceId, dispatch]);
  const getNextPage = useCallback(
    next => {
      if (next && !fetching) {
        dispatch(setFetching(true));
        dispatch(
          fetchNextPage({
            accessToken,
            deviceId,
            nextUrl: next,
            filterName: previousSelectedFilterRef.current,
          }),
        );
      }
    },
    [accessToken, deviceId, dispatch, fetching],
  );
  const getBreadcrumbsData = () => breadcrumbsBrowseParser(selectedTab, filterName);
  const SEO = BrowseSEO;
  if (filterName) {
    SEO.canonicalUrl = `${BaseUrl}/browse/${filterName}/`;
    SEO.ogUrl = `${BaseUrl}/browse/${filterName}/`;
    SEO.twitterUrl = `${BaseUrl}/browse/${filterName}/`;
  } else {
    SEO.canonicalUrl = `${BaseUrl}/browse/`;
    SEO.ogUrl = `${BaseUrl}/browse/`;
    SEO.twitterUrl = `${BaseUrl}/browse/`;
  }

  return (
    <section className={Style.container}>
      {setHelmetTags(SEO)}
      <h2 className={Style.title}>{browseHeader}</h2>
      <Filter isScrolled={false} filters={filters} theme={Style} isCarousel />
      <Result
        account={account}
        favoritesManager={favoritesManager}
        fetching={fetching}
        items={items}
        loadMore={getNextPage}
        next={browseContext?.next}
        posterType={BROWSE_POSTER}
        theme={Style}
        type={browseResults?.[selectedTab]?.type}
      />
      <BreadcrumbsComponent data={getBreadcrumbsData()} customStyle={Style.breadcrumbs} />
    </section>
  );
};
Browse.fetchData = ({ store, accessToken, params }) =>
  store.dispatch(dispatch =>
    dispatch(fetchBrowse(accessToken)).then(async response => {
      const { browseFilterId } = params;
      const filter = browseFilterId ? `${spaceToDash(browseFilterId)}` : null;
      dispatch(selectTab(filter));
      const data = response?.payload?.data;
      const browsePanel = data?.panels ?? {};
      const filters = browsePanel?.member ?? [];
      const browseResults = reduceBrowseResults({ filters });
      const { next } = browseResults?.[filter] ?? {};
      if (next)
        return dispatch(
          fetchNextPage({
            accessToken,
            nextUrl: next,
            filterName: filter,
          }),
        );
      return response;
    }),
  );

export default Browse;

Browse.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  deviceId: PropTypes.string,
  favoritesManager: PropTypes.shape({}),
  location: PropTypes.shape({}),
};
